<template>
  <div class="product-carousel critical-component-clear">
    <div class="product-carousel__container">
      <div
        v-if="products.length > 0"
        ref="carousel"
        class="product-carousel__carousel splide"
        :js-carousel="`${liquid.sectionId}`"
      >
        <div class="product-carousel__track splide__track">
          <ul class="product-carousel__slides splide__list">
            <li
              v-for="(product, index) of products"
              :key="`splide-${product.id}-${index}`"
              class="product-carousel__slide splide__slide"
            >
              <product-card
                :product="product"
                block-class="product-carousel"
                enable-badges
                :enable-swatches="true"
                :index="index"
                :sizes="sizes"
                :swatch-behavior="liquid.swatchBehavior"
              />
            </li>
          </ul>
        </div>

        <div class="product-carousel__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
            :aria-label="$string('accessibility.carousel.previous')"
          >
            <previous-arrow />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
            :aria-label="$string('accessibility.carousel.next')"
          >
            <next-arrow />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Product carousel component.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} handle - Product handles.
 *
 */

import Carousel from '~/api/carousel'

import NextArrow from '~icons/direction/arrow/right.svg'
import PreviousArrow from '~icons/direction/arrow/left.svg'

import product from '~/graphql/queries/product.gql'
import ProductCard from '~async/product-card/product-card'

import { formatProduct } from '~/helpers/format'

export default {
  name: 'ProductCarousel',

  components: {
    NextArrow,
    PreviousArrow,
    ProductCard,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    handles: {
      type: Array,
      default: () => [],
      required: true,
    },
    swatchBehavior: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      slide: {
        carousel: false,
        config: {
          arrows: false,
          fixedWidth: '80%',
          gap: 'var(--spacing-xs)',
          padding: {
            left: 'var(--spacing-xs)',
            right: 'var(--spacing-xs)',
          },
          pagination: false,
          perMove: 1,
          omitEnd: true,
          snap: true,
          speed: 1500,
          type: 'slide',
          breakpoints: {
            1024: {
              arrows: true,
              drag: false,
              fixedWidth: '41%',
              wheel: false,
            },
            1366: {
              arrows: true,
              fixedWidth: '30%',
            },
            1700: {
              arrows: true,
              fixedWidth: '23%',
            },
          },
          wheel: true,
          wheelSleep: 400,
          releaseWheel: true,
        },
      },
      products: [
        false,
        false,
        false,
        false,
        false,
      ],
      queue: [],
      state: 'loading',
    }
  },

  computed: {

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `
        (min-width: 1700px) 23vw,
        (min-width: 1366px) 30vw,
        (min-width: 1024px) 41vw, 80vw
      `
    },
  },

  watch: {
    products() {
      this.$nextTick().then(() => {
        if (this.$refs.carousel) {
          this.initCarousel()
        }
      })
    },
  },

  mounted() {
    if (!this.handles) {
      return
    }

    if (this.handles.length < 5) {
      this.products.length = this.handles.length
    }

    this.queryProducts()
    if (this.$refs.carousel) {
      this.initCarousel()
    }
  },

  methods: {

    /**
     * Query products from GraphQL API.
     */
    async queryProducts() {
      try {
        this.queue = this.handles.map((handle) => {
          return cnvs.Query({
            query: product,
            variables: {
              country: this.$variable('store.country'),
              handle,
              language: this.$variable('store.language'),
            },
          })
        })

        const response = await Promise.allSettled(this.queue)

        const products = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          return formatProduct(promise.value)
        }).filter(Boolean)

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        /**
         * If no products returned then error.
         */
        if (!products.length) {
          this.state = 'error'
          return
        }

        this.products = products
        this.state = 'ready'
      } catch (error) {
        this.state = 'error'
      }
    },

    /**
     * Initalize Carousel
     */
    initCarousel() {
      if (this.slide.carousel) {
        this.slide.carousel.destroy()
      }

      this.slide.carousel = Carousel(`[js-carousel="${this.liquid.sectionId}"]`, {
        ...this.slide.config,
      })

      this.slide.carousel.mount()
    },
  },
}
</script>

<style lang="scss">
@import './product-carousel';
</style>
